// src/components/proposal/AiPricingPanel.tsx
// Renders AI-derived market-comp pricing: a transparent rate table with basis +
// confidence, the burdened cost roll-up, and the clarifying questions the user
// should confirm. Answers are collected and passed back via onReprice so the
// pricing can be re-derived with the user's inputs as authoritative.

import React, { useState } from 'react';
import { DollarSign, Sparkles, HelpCircle, RefreshCw, ShieldCheck } from 'lucide-react';
import type { AiPricing } from '../../types/deliverables';

export interface PricingAnswers {
  fringe_rate?: number;
  overhead_rate?: number;
  ga_rate?: number;
  fee_rate?: number;
  locality?: string;
  gsa_schedule?: boolean;
  escalation_rate?: number;
  rate_overrides?: Record<string, number>;
}

interface AiPricingPanelProps {
  aiPricing: AiPricing;
  onReprice?: (answers: PricingAnswers) => void | Promise<void>;
  isRepricing?: boolean;
  className?: string;
}

const usd = (n: number) =>
  `$${Math.round(n).toLocaleString('en-US')}`;

const confidenceColor = (c: string) =>
  c === 'high' ? 'text-success bg-success-bg'
  : c === 'medium' ? 'text-fit-moderate bg-fit-moderate-bg'
  : 'text-fit-moderate bg-orange-100';

const impactColor = (i: string) =>
  i === 'high' ? 'border-red-300 bg-urgent-bg'
  : i === 'medium' ? 'border-yellow-300 bg-fit-moderate-bg'
  : 'border-bw-border bg-bw-surface-subtle';

export function AiPricingPanel({ aiPricing, onReprice, isRepricing = false, className = '' }: AiPricingPanelProps) {
  const { cost_rollup, indirect_assumptions, clarifying_questions = [], notes } = aiPricing || {};
  const [answers, setAnswers] = useState<Record<string, string>>({});

  if (!cost_rollup) return null;

  // Map a clarifying-question answer (by field) into the PricingAnswers shape.
  const buildAnswers = (): PricingAnswers => {
    const out: PricingAnswers = {};
    for (const q of clarifying_questions) {
      const raw = answers[q.id];
      if (raw === undefined || raw === '') continue;
      switch (q.field) {
        case 'fringe_rate': out.fringe_rate = parseFloat(raw); break;
        case 'overhead_rate': out.overhead_rate = parseFloat(raw); break;
        case 'ga_rate': out.ga_rate = parseFloat(raw); break;
        case 'fee_rate': out.fee_rate = parseFloat(raw); break;
        case 'escalation_rate': out.escalation_rate = parseFloat(raw); break;
        case 'locality': out.locality = raw; break;
        case 'gsa_schedule': out.gsa_schedule = raw === 'true' || raw === 'Yes'; break;
        default: break;
      }
    }
    return out;
  };

  const hasAnswers = Object.values(answers).some((v) => v !== undefined && v !== '');
  const ind = indirect_assumptions;

  return (
    <div className={`rounded-xl border border-bw-border bg-bw-surface ${className}`}>
      {/* Header / totals */}
      <div className="flex items-center justify-between p-4 border-b border-bw-border">
        <div className="flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-bw-primary" />
          <h3 className="font-semibold text-bw-ink">AI-Derived Pricing</h3>
        </div>
        <div className="text-right">
          <div className="text-2xl font-bold text-bw-ink">{usd(cost_rollup.total_price)}</div>
          <div className="text-xs text-bw-muted">
            {cost_rollup.total_hours.toLocaleString()} hrs · {usd(cost_rollup.blended_loaded_rate)}/hr blended
          </div>
        </div>
      </div>

      {/* Rate table */}
      <div className="p-4 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-bw-muted border-b border-bw-border">
              <th className="py-2 pr-2 font-medium">Labor Category</th>
              <th className="py-2 px-2 font-medium text-right">Hours</th>
              <th className="py-2 px-2 font-medium text-right">Base $/hr</th>
              <th className="py-2 px-2 font-medium text-right">Loaded $/hr</th>
              <th className="py-2 px-2 font-medium">Basis</th>
              <th className="py-2 pl-2 font-medium">Confidence</th>
            </tr>
          </thead>
          <tbody>
            {cost_rollup.by_category.map((c, i) => (
              <tr key={i} className="border-b border-gray-50" title={c.rationale || ''}>
                <td className="py-2 pr-2 text-bw-ink">
                  {c.category}
                  {c.sca_title && <span className="block text-xs text-bw-muted">SCA: {c.sca_title}</span>}
                </td>
                <td className="py-2 px-2 text-right text-bw-ink">{c.hours.toLocaleString()}</td>
                <td className="py-2 px-2 text-right text-bw-ink">{usd(c.base_hourly)}</td>
                <td className="py-2 px-2 text-right font-medium text-bw-ink">{usd(c.loaded_hourly)}</td>
                <td className="py-2 px-2 text-bw-muted">{c.basis}</td>
                <td className="py-2 pl-2">
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${confidenceColor(c.confidence)}`}>
                    {c.confidence}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Cost build-up */}
      <div className="px-4 pb-4 grid grid-cols-2 sm:grid-cols-3 gap-2 text-sm">
        <Stat label="Direct Labor" value={usd(cost_rollup.direct_labor)} />
        <Stat label={`Fringe (${ind?.fringe_pct ?? 0}%)`} value={usd(cost_rollup.fringe)} />
        <Stat label={`Overhead (${ind?.overhead_pct ?? 0}%)`} value={usd(cost_rollup.overhead)} />
        <Stat label={`G&A (${ind?.ga_pct ?? 0}%)`} value={usd(cost_rollup.ga)} />
        <Stat label={`Fee (${ind?.fee_pct ?? 0}%)`} value={usd(cost_rollup.fee)} />
        <Stat label="Total Price" value={usd(cost_rollup.total_price)} emphasize />
      </div>

      {notes && (
        <p className="px-4 pb-3 text-xs text-bw-muted flex items-start gap-1">
          <ShieldCheck className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" /> {notes}
        </p>
      )}

      {/* Clarifying questions */}
      {clarifying_questions.length > 0 && (
        <div className="border-t border-bw-border p-4">
          <div className="flex items-center gap-2 mb-3">
            <HelpCircle className="w-4 h-4 text-bw-primary" />
            <h4 className="font-medium text-bw-ink text-sm">
              Confirm these assumptions to firm up the price
            </h4>
          </div>
          <div className="space-y-3">
            {clarifying_questions.map((q) => (
              <div key={q.id} className={`rounded-lg border p-3 ${impactColor(q.impact)}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1">
                    <p className="text-sm font-medium text-bw-ink">{q.question}</p>
                    <p className="text-xs text-bw-muted mt-0.5">{q.why_it_matters}</p>
                    <p className="text-xs text-bw-muted mt-0.5">Assumed: {q.current_assumption}</p>
                  </div>
                  <QuestionInput
                    q={q}
                    value={answers[q.id] ?? ''}
                    onChange={(v) => setAnswers((prev) => ({ ...prev, [q.id]: v }))}
                  />
                </div>
              </div>
            ))}
          </div>

          {onReprice && (
            <button
              onClick={() => onReprice(buildAnswers())}
              disabled={isRepricing || !hasAnswers}
              className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-bw-primary text-white text-sm font-medium disabled:opacity-50 hover:bg-indigo-700"
            >
              <RefreshCw className={`w-4 h-4 ${isRepricing ? 'animate-spin' : ''}`} />
              {isRepricing ? 'Re-pricing…' : 'Apply answers & re-price'}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, emphasize }: { label: string; value: string; emphasize?: boolean }) {
  return (
    <div className={`rounded-lg px-3 py-2 ${emphasize ? 'bg-bw-primary-tint' : 'bg-bw-surface-subtle'}`}>
      <div className="text-xs text-bw-muted">{label}</div>
      <div className={`font-semibold ${emphasize ? 'text-bw-primary' : 'text-bw-ink'}`}>
        <DollarSign className="w-3 h-3 inline -mt-0.5" />{value.replace('$', '')}
      </div>
    </div>
  );
}

function QuestionInput({
  q, value, onChange,
}: {
  q: AiPricing['clarifying_questions'][number];
  value: string;
  onChange: (v: string) => void;
}) {
  if (q.input_type === 'boolean') {
    return (
      <select value={value} onChange={(e) => onChange(e.target.value)} className="text-sm border border-bw-border rounded-md px-2 py-1 w-28">
        <option value="">—</option>
        <option value="Yes">Yes</option>
        <option value="No">No</option>
      </select>
    );
  }
  if (q.input_type === 'choice' && q.options) {
    return (
      <select value={value} onChange={(e) => onChange(e.target.value)} className="text-sm border border-bw-border rounded-md px-2 py-1 w-32">
        <option value="">—</option>
        {q.options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    );
  }
  const isNum = q.input_type === 'percent' || q.input_type === 'currency';
  return (
    <div className="relative">
      <input
        type={isNum ? 'number' : 'text'}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={q.input_type === 'percent' ? '%' : ''}
        className="text-sm border border-bw-border rounded-md px-2 py-1 w-24"
      />
    </div>
  );
}
